import {
  Content,
  Body,
  editableFieldStyle,
  Tabs,
  Tab,
  Group,
  FieldRow,
  ColorControls,
  ColorValue,
  colorHexStyle,
  CompactFieldRow,
  PreviewArea,
  Title,
  isEffectMenuTarget,
  ColorRow,
  ButtonAnchor,
  Header,
  EnableLabel,
  Summary,
  Notice,
  Heading,
  Actions,
} from './textEffectStyles';
import { isEqual } from '../../../../../../../utils/isEqual';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  forwardRef,
  useCallback,
  useMemo,
  useEffect,
  useFocusOut,
  useRef,
  useResizeEffect,
  useState,
} from '@web-stories-wp/react';
import { __, sprintf } from '@web-stories-wp/i18n';
import {
  Button,
  Checkbox,
  HexInput,
  Icons,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  BUTTON_TYPES,
  Pill,
  PLACEMENT,
} from '@web-stories-wp/design-system';
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import { Row } from '../../../form';
import {
  createSolidFromString,
  getHexFromSolid,
} from '@web-stories-wp/patterns';
import Popup from '../../../popup';
import useInspector from '../../../inspector/useInspector';
import useFocusTrapping from '../../../../utils/useFocusTrapping';
import { getCommonValue, focusStyle } from '../../shared';
import { StyledNumericInput } from './elements';
import {
  DEFAULTS,
  OPTIONS,
  FIELDS,
  GLOSS_CONTOUR_OPTIONS,
  TECHNIQUE_OPTIONS,
  settingsFor,
  bevelSettingsFor,
  patchSettings,
  getSetting,
} from './textDepthSettings';
import TextDepthPreview from './textDepthPreview';
import { getTextEffectAlignmentUpdate } from '../../../../elements/text/utils/getTextEffectAlignmentUpdate';
import LightDirectionDial from './lightDirectionDial';
import {
  toPhotoshopLightAngle,
  toStudioLightDirection,
} from './photoshopLightAngle';
import { bevelContourTable } from '../../../../elements/text/utils/getBevelDepth';
import TextEffectBlendMode from './textEffectBlendMode';
import TextEffectNumericField from './textEffectNumericField';
import TextEffectSelect from './textEffectSelect';
import useFrameUpdates from './useFrameUpdates';
import useTransform from '../../../transform/useTransform';
import { lightDirectionPreviewId } from '../../../../elements/text/utils/lightDirectionPreviewId';
import EffectColorInput from './effectColorInput';

const ModeSelector = styled.div`
  display: flex;
  gap: 8px;
  button {
    flex: 1;
    min-width: 0;
  }
`;
const ContourFields = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  display: grid;
  gap: 12px;
  &:disabled {
    opacity: 0.5;
  }
`;
const ProfileChoices = styled.div`
  display: flex;
  gap: 6px;
`;
const ProfileChoice = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  padding: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border.defaultNormal};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  background: transparent;
  color: ${({ theme }) => theme.colors.fg.primary};
  font: inherit;
  cursor: pointer;
  svg {
    width: 24px;
    height: 20px;
    flex-shrink: 0;
  }
  &[aria-pressed='true'] {
    border-color: ${({ theme }) => theme.colors.interactiveBg.primaryNormal};
    background: ${({ theme }) => theme.colors.interactiveBg.primaryNormal};
    color: ${({ theme }) => theme.colors.bg.primary};
  }
  &:hover:enabled {
    border-color: ${({ theme }) => theme.colors.border.defaultHover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }
  &:disabled {
    cursor: default;
  }
`;
const CONTOUR_PROFILES = [
  { id: 'linear', label: __('Linear', 'web-stories') },
  { id: 'ring', label: __('Ring', 'web-stories') },
].map((profile) => ({
  ...profile,
  // Use the renderer's curve for each thumbnail, independent of authored range.
  points: bevelContourTable(profile.id, 100)
    .split(' ')
    .map(
      (value, index) => `${4 + (index / 256) * 92},${44 - Number(value) * 40}`
    )
    .join(' '),
}));
const DirectionRow = styled.div`
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  align-items: center;
  gap: 20px;
`;
const DirectionFields = styled.div`
  display: grid;
  gap: 10px;
  min-width: 0;
`;
const TABS = [
  { id: 'essentials', label: __('Essentials', 'web-stories') },
  { id: 'advanced', label: __('Advanced', 'web-stories') },
];
const DIALOG_ID = 'text-depth-settings-dialog';
const POPUP_SPACING = { x: 20 };

const LightDirectionField = forwardRef(function LightDirectionField(
  { value, onChange, onPreviewChange, altitude },
  ref
) {
  const [previewValue, setPreviewValue] = useState(null);
  const { queue, cancel } = useFrameUpdates(({ angle }) =>
    setPreviewValue(angle)
  );
  const commit = (angle) => {
    cancel();
    setPreviewValue(null);
    // Keep the exact saved value when a displayed angle has not changed.
    // Converting fractional angles back can introduce a floating-point delta,
    // which would otherwise count as an edit and upgrade a legacy effect.
    if (value !== MULTIPLE_VALUE && angle === toPhotoshopLightAngle(value))
      return;
    onChange(toStudioLightDirection(angle));
  };
  const numericChange = (_event, next) => {
    if (next !== '' && next !== MULTIPLE_VALUE && Number.isFinite(Number(next)))
      commit(Math.min(180, Math.max(-180, Number(next))));
  };
  const photoshopValue =
    value === MULTIPLE_VALUE ? value : toPhotoshopLightAngle(value);
  const displayedValue = previewValue ?? photoshopValue;
  return (
    <DirectionRow>
      <LightDirectionDial
        ref={ref}
        value={photoshopValue === MULTIPLE_VALUE ? null : photoshopValue}
        onChange={commit}
        onPreviewChange={(angle) => {
          queue({ angle });
          onPreviewChange(toStudioLightDirection(angle));
        }}
      />
      <DirectionFields>
        <CompactFieldRow>
          <span>{__('Angle', 'web-stories')}</span>
          <StyledNumericInput
            aria-label={__('Depth light direction', 'web-stories')}
            unit="°"
            min={-180}
            max={180}
            canBeNegative
            isFloat
            value={displayedValue}
            isIndeterminate={displayedValue === MULTIPLE_VALUE}
            placeholder={
              displayedValue === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : null
            }
            containerStyleOverride={editableFieldStyle}
            onChange={numericChange}
            onBlur={numericChange}
          />
        </CompactFieldRow>
        {altitude}
      </DirectionFields>
    </DirectionRow>
  );
});
LightDirectionField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  onPreviewChange: PropTypes.func.isRequired,
  altitude: PropTypes.node.isRequired,
};

function DepthDialog({ anchor, elements, onClose, onUpdate, onResize }) {
  const containerRef = useRef();
  const directionRef = useRef();
  const pushTransform = useTransform(({ actions }) => actions.pushTransform);
  const previewIds = useMemo(
    () => elements.map(({ id }) => lightDirectionPreviewId(id)),
    [elements]
  );
  const { queue: queueDirection, cancel: cancelDirection } = useFrameUpdates(
    ({ angle }) => {
      previewIds.forEach((id) =>
        pushTransform?.(id, {
          staticTransformation: true,
          updates: { lightDirection: angle },
        })
      );
    },
    32
  );
  const clearDirection = useCallback(() => {
    cancelDirection();
    previewIds.forEach((id) => pushTransform?.(id, null));
  }, [cancelDirection, previewIds, pushTransform]);
  useEffect(() => clearDirection, [clearDirection]);
  const previewSettings = useMemo(() => settingsFor(elements[0]), [elements]);
  useResizeEffect(containerRef, onResize, [onResize]);
  const [section, setSection] = useState('essentials');
  const values = elements.map((element) => ({
    ...element,
    textDepth: bevelSettingsFor(element),
  }));
  const valueFor = (key) =>
    getCommonValue(values, ({ textDepth }) => getSetting(textDepth, key));
  const mode = valueFor('mode');
  const update = (getSettings) => {
    if (
      !elements.some(
        (element) => !isEqual(settingsFor(element), getSettings(element))
      )
    )
      return;
    const ids = new Set(elements.map(({ id }) => id));
    onUpdate((element) => {
      if (!ids.has(element.id)) return {};
      const next = getSettings(element);
      return isEqual(settingsFor(element), next)
        ? {}
        : {
            textDepth: next,
            ...getTextEffectAlignmentUpdate({ ...element, textDepth: next }),
          };
    });
  };
  const { queue, flush, cancel } = useFrameUpdates((changes) => {
    const patch = Object.fromEntries(
      Object.entries(changes).filter(([key, value]) => valueFor(key) !== value)
    );
    // Unchanged blur must not create history or upgrade a legacy recipe.
    if (!Object.keys(patch).length) return;
    update((element) => patchSettings(bevelSettingsFor(element), patch));
  });
  const reset = () => {
    cancel();
    update((element) => ({
      ...DEFAULTS,
      mode: settingsFor(element).mode,
    }));
  };
  const previewChange = (key, value) => queue({ [key]: value });
  const change = (key, value) => {
    previewChange(key, value);
    flush();
  };
  const closeAndRefocus = useCallback(() => {
    directionRef.current?.finish();
    flush();
    anchor.current?.querySelector('button')?.focus();
    onClose();
  }, [anchor, flush, onClose]);
  useFocusOut(
    containerRef,
    (event) => {
      const target =
        event.type === 'focusout' ? event.relatedTarget : event.target;
      if (
        !containerRef.current?.contains(target) &&
        !anchor.current?.contains(target) &&
        !isEffectMenuTarget(containerRef.current, target)
      ) {
        directionRef.current?.finish();
        flush();
        onClose();
      }
    },
    [anchor, flush, onClose]
  );
  useFocusTrapping({ ref: containerRef });
  useEffect(() => {
    const firstControl =
      containerRef.current?.querySelector('[aria-pressed="true"]') ||
      containerRef.current?.querySelector('button');
    firstControl?.focus();
  }, []);
  const field = (key, options = {}) => (
    <TextEffectNumericField
      key={key}
      field={FIELDS.find((field) => field.key === key)}
      value={valueFor(key)}
      onChange={(value) => change(key, value)}
      {...options}
    />
  );
  const colorField = (group, label, colorLabel) => {
    const value = valueFor(`${group}.color`);
    const isColor = typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
    return (
      <ColorRow>
        <span>{label}</span>
        <ColorControls>
          <ColorValue>
            <EffectColorInput
              aria-label={
                group === 'highlight'
                  ? __('Choose highlight color', 'web-stories')
                  : __('Choose shadow color', 'web-stories')
              }
              value={isColor ? value : '#808080'}
              onPreviewChange={(value) =>
                previewChange(`${group}.color`, value)
              }
              onChange={(value) => change(`${group}.color`, value)}
              onBlur={flush}
            />
            <HexInput
              data-text-effect-hex=""
              aria-label={colorLabel}
              onChangeCapture={(event) => {
                const hex = event.target.value.trim().replace(/^#/, '');
                if (/^[0-9a-f]{6}$/i.test(hex))
                  change(`${group}.color`, `#${hex.toLowerCase()}`);
              }}
              value={isColor ? createSolidFromString(value) : null}
              isIndeterminate={value === MULTIPLE_VALUE}
              placeholder={
                value === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : ''
              }
              containerStyleOverride={colorHexStyle}
              onChange={(solid) =>
                change(`${group}.color`, `#${getHexFromSolid(solid, true)}`)
              }
            />
          </ColorValue>
          {field(`${group}.opacity`, { compact: true })}
        </ColorControls>
      </ColorRow>
    );
  };
  return (
    <Content
      id={DIALOG_ID}
      role="dialog"
      aria-label={__('Depth settings', 'web-stories')}
      ref={containerRef}
      onKeyDownCapture={(event) => {
        // Let HexInput's native handler cancel before dismissal/refocusing
        // blurs the field. Already applied effect settings stay committed.
        if (
          event.key === 'Escape' &&
          !isEffectMenuTarget(containerRef.current, event.target)
        ) {
          if (event.target.hasAttribute('data-text-effect-hex')) {
            Promise.resolve().then(closeAndRefocus);
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          closeAndRefocus();
        }
      }}
      onKeyDown={(event) => {
        // Portal keyboard events must not submit the enclosing inspector form.
        if (event.key === 'Enter' && event.target.tagName === 'INPUT')
          event.preventDefault();
      }}
    >
      <Header>
        <Title>{__('Depth settings', 'web-stories')}</Title>
        <Button
          type={BUTTON_TYPES.TERTIARY}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SQUARE}
          aria-label={__('Close depth settings', 'web-stories')}
          onClick={closeAndRefocus}
        >
          <Icons.Cross />
        </Button>
      </Header>
      <PreviewArea>
        {__('Effect preview', 'web-stories')}
        <TextDepthPreview
          textDepth={previewSettings}
          elementId={elements[0].id}
        />
      </PreviewArea>
      <Tabs
        role="tablist"
        aria-label={__('Depth settings sections', 'web-stories')}
        onKeyDown={(event) => {
          const index = TABS.findIndex(({ id }) => id === section);
          const next =
            event.key === 'ArrowRight'
              ? (index + 1) % TABS.length
              : event.key === 'ArrowLeft'
              ? (index + TABS.length - 1) % TABS.length
              : event.key === 'Home'
              ? 0
              : event.key === 'End'
              ? TABS.length - 1
              : null;
          if (next === null) return;
          event.preventDefault();
          directionRef.current?.finish();
          setSection(TABS[next].id);
          event.currentTarget.querySelectorAll('[role="tab"]')[next]?.focus();
        }}
      >
        {TABS.map(({ id, label }) => (
          <Tab
            key={id}
            type="button"
            role="tab"
            id={`${DIALOG_ID}-tab-${id}`}
            aria-controls={`${DIALOG_ID}-panel-${id}`}
            aria-selected={section === id}
            tabIndex={section === id ? 0 : -1}
            onClick={() => {
              directionRef.current?.finish();
              setSection(id);
            }}
          >
            {label}
          </Tab>
        ))}
      </Tabs>
      <Body
        key={section}
        role="tabpanel"
        id={`${DIALOG_ID}-panel-${section}`}
        aria-labelledby={`${DIALOG_ID}-tab-${section}`}
      >
        {elements.length > 1 && (
          <Notice>
            {__(
              'Preview uses the first layer’s settings. Changes apply to all selected text layers.',
              'web-stories'
            )}
          </Notice>
        )}
        {elements.some(({ textDepth }) => textDepth?.version === 1) && (
          <Notice>
            {__(
              'Editing a setting updates this layer to the expanded bevel treatment.',
              'web-stories'
            )}
          </Notice>
        )}
        {section === 'essentials' && (
          <>
            <Group>
              <ModeSelector
                role="group"
                aria-label={__('Depth mode', 'web-stories')}
              >
                {OPTIONS.filter(({ id }) => id !== 'off').map(
                  ({ id, label }) => (
                    <Pill
                      key={id}
                      type="button"
                      isActive={mode === id}
                      aria-pressed={mode === id}
                      pillStyleOverride={focusStyle}
                      onClick={() => change('mode', id)}
                    >
                      {label}
                    </Pill>
                  )
                )}
              </ModeSelector>
              <TextEffectSelect
                label={__('Depth technique', 'web-stories')}
                visibleLabel={__('Technique', 'web-stories')}
                options={TECHNIQUE_OPTIONS}
                value={valueFor('technique')}
                onChange={(value) => change('technique', value)}
              />
              {field('size')}
              {field('softness')}
            </Group>
            <Group>
              <Heading>{__('Lighting', 'web-stories')}</Heading>
              <LightDirectionField
                ref={directionRef}
                value={valueFor('lightDirection')}
                altitude={field('altitude', { withSlider: false })}
                onPreviewChange={(angle) => queueDirection({ angle })}
                onChange={(angle) => {
                  change('lightDirection', angle);
                  clearDirection();
                }}
              />
              {colorField(
                'highlight',
                __('Highlight', 'web-stories'),
                __('Highlight color', 'web-stories')
              )}
              {colorField(
                'shadow',
                __('Shadow', 'web-stories'),
                __('Shadow color', 'web-stories')
              )}
            </Group>
          </>
        )}
        {section === 'advanced' && (
          <>
            <Group>
              {field('depth')}
              <TextEffectSelect
                label={__('Depth gloss contour', 'web-stories')}
                visibleLabel={__('Gloss contour', 'web-stories')}
                options={GLOSS_CONTOUR_OPTIONS}
                value={valueFor('glossContour')}
                onChange={(value) => change('glossContour', value)}
              />
              <TextEffectBlendMode
                label={__('Highlight blend mode', 'web-stories')}
                visibleLabel={__('Highlight blend', 'web-stories')}
                value={valueFor('highlight.blendMode')}
                onChange={(value) => change('highlight.blendMode', value)}
              />
              <TextEffectBlendMode
                label={__('Shadow blend mode', 'web-stories')}
                visibleLabel={__('Shadow blend', 'web-stories')}
                value={valueFor('shadow.blendMode')}
                onChange={(value) => change('shadow.blendMode', value)}
              />
            </Group>
            <Group>
              <FieldRow>
                <span>{__('Contour', 'web-stories')}</span>
                <Checkbox
                  aria-label={__('Contour enabled', 'web-stories')}
                  checked={valueFor('contour.enabled') === true}
                  ref={(input) => {
                    if (input)
                      input.indeterminate =
                        valueFor('contour.enabled') === MULTIPLE_VALUE;
                  }}
                  onChange={(event) =>
                    change('contour.enabled', event.target.checked)
                  }
                />
              </FieldRow>
              <ContourFields disabled={valueFor('contour.enabled') === false}>
                <FieldRow>
                  <span>{__('Profile', 'web-stories')}</span>
                  <ProfileChoices
                    role="group"
                    aria-label={__('Contour profile', 'web-stories')}
                  >
                    {CONTOUR_PROFILES.map(({ id, label, points }) => (
                      <ProfileChoice
                        key={id}
                        type="button"
                        aria-pressed={valueFor('contour.profile') === id}
                        onClick={() => change('contour.profile', id)}
                      >
                        <svg
                          viewBox="0 0 100 48"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <polyline
                            points={points}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {label}
                      </ProfileChoice>
                    ))}
                  </ProfileChoices>
                </FieldRow>
                {field('contour.range')}
              </ContourFields>
            </Group>
          </>
        )}
      </Body>
      <Actions>
        <Button
          type={BUTTON_TYPES.TERTIARY}
          size={BUTTON_SIZES.SMALL}
          onClick={reset}
        >
          {__('Reset depth', 'web-stories')}
        </Button>
        <Button
          type={BUTTON_TYPES.SECONDARY}
          size={BUTTON_SIZES.SMALL}
          onClick={closeAndRefocus}
        >
          {__('Done', 'web-stories')}
        </Button>
      </Actions>
    </Content>
  );
}

DepthDialog.propTypes = {
  anchor: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  elements: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onResize: PropTypes.func.isRequired,
};

function TextDepthControls({ selectedElements, pushUpdate }) {
  const [session, setSession] = useState(null);
  const anchor = useRef();
  const {
    refs: { inspector },
  } = useInspector();
  const selectionKey = selectedElements.map(({ id }) => id).join(',');
  const mode = getCommonValue(
    selectedElements,
    (element) => settingsFor(element).mode
  );
  const modeLabel =
    OPTIONS.find(({ id }) => id === mode)?.label || MULTIPLE_DISPLAY_VALUE;
  const summaryValue = (key) => {
    const value = getCommonValue(selectedElements, (element) =>
      getSetting(bevelSettingsFor(element), key)
    );
    if (value === MULTIPLE_VALUE) return MULTIPLE_DISPLAY_VALUE;
    return key === 'lightDirection' ? toPhotoshopLightAngle(value) : value;
  };
  const label =
    mode === 'off' || mode === MULTIPLE_VALUE
      ? modeLabel
      : sprintf(
          /* translators: 1: depth mode, 2: size in pixels, 3: lighting angle. */
          __('%1$s · %2$s px · %3$s°', 'web-stories'),
          modeLabel,
          summaryValue('size'),
          summaryValue('lightDirection')
        );
  const isOpen = session?.key === selectionKey;
  useEffect(() => {
    setSession(null);
  }, [selectionKey]);
  return (
    <>
      <Row>
        <EnableLabel>
          <Checkbox
            aria-label={__('Depth enabled', 'web-stories')}
            checked={mode !== 'off' && mode !== MULTIPLE_VALUE}
            ref={(input) => {
              if (input) input.indeterminate = mode === MULTIPLE_VALUE;
            }}
            onChange={(event) => {
              const enabled = event.target.checked;
              pushUpdate(
                (element) => {
                  const textDepth = {
                    ...settingsFor(element),
                    mode: enabled
                      ? settingsFor(element).mode === 'recessed'
                        ? 'recessed'
                        : 'raised'
                      : 'off',
                  };
                  return {
                    textDepth,
                    ...getTextEffectAlignmentUpdate({ ...element, textDepth }),
                  };
                },
                true,
                { skipPresubmit: true }
              );
            }}
          />
          {__('Depth', 'web-stories')}
        </EnableLabel>
        <ButtonAnchor ref={anchor}>
          <Button
            type={BUTTON_TYPES.TERTIARY}
            size={BUTTON_SIZES.SMALL}
            aria-haspopup="dialog"
            aria-label={__('Edit depth…', 'web-stories')}
            aria-expanded={isOpen}
            aria-controls={isOpen ? DIALOG_ID : undefined}
            onClick={(event) => {
              event.preventDefault();
              setSession(isOpen ? null : { key: selectionKey });
            }}
          >
            {__('Edit…', 'web-stories')}
          </Button>
        </ButtonAnchor>
      </Row>
      <Row>
        <Summary>{label}</Summary>
      </Row>
      <Popup
        anchor={anchor}
        dock={inspector}
        isOpen={isOpen}
        placement={PLACEMENT.LEFT_START}
        spacing={POPUP_SPACING}
        renderContents={({ propagateDimensionChange }) => (
          <DepthDialog
            anchor={anchor}
            onResize={propagateDimensionChange}
            elements={selectedElements}
            onClose={() => setSession(null)}
            onUpdate={(update) => {
              // Depth changes paint only: use the inspector's synchronous path so
              // closing the popup cannot leave a deferred update on another selection.
              pushUpdate(update, true, { skipPresubmit: true });
            }}
          />
        )}
      />
    </>
  );
}
TextDepthControls.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};
export default TextDepthControls;
